import { StorageAuthError, StorageError } from '../../errors.js';
const DRIVE_FILES = 'https://www.googleapis.com/drive/v3/files';
const DRIVE_UPLOAD = 'https://www.googleapis.com/upload/drive/v3/files';
const PROVIDER_ID = 'google-drive';
function authHeaders(token) {
    return { Authorization: `Bearer ${token}` };
}
async function ensureOk(response, op) {
    if (response.ok)
        return;
    if (response.status === 401 || response.status === 403) {
        throw new StorageAuthError(PROVIDER_ID, op);
    }
    const body = await response.text().catch(() => '');
    throw new StorageError(`drive ${op} failed (${response.status}): ${body}`, PROVIDER_ID, op);
}
export async function listFolderFiles(token, folderId) {
    const out = [];
    let pageToken;
    do {
        const url = new URL(DRIVE_FILES);
        url.searchParams.set('q', `'${folderId}' in parents and trashed=false`);
        url.searchParams.set('fields', 'nextPageToken,files(id,name,modifiedTime,mimeType)');
        url.searchParams.set('pageSize', '1000');
        if (pageToken)
            url.searchParams.set('pageToken', pageToken);
        const response = await fetch(url, { headers: authHeaders(token) });
        await ensureOk(response, 'list');
        const data = (await response.json());
        out.push(...(data.files ?? []));
        pageToken = data.nextPageToken;
    } while (pageToken);
    return out;
}
export async function getFileText(token, fileId) {
    const url = `${DRIVE_FILES}/${encodeURIComponent(fileId)}?alt=media`;
    const response = await fetch(url, { headers: authHeaders(token) });
    await ensureOk(response, 'get');
    return response.text();
}
export async function getFileBlob(token, fileId) {
    const url = `${DRIVE_FILES}/${encodeURIComponent(fileId)}?alt=media`;
    const response = await fetch(url, { headers: authHeaders(token) });
    await ensureOk(response, 'getBlob');
    return response.blob();
}
export async function deleteFile(token, fileId) {
    const url = `${DRIVE_FILES}/${encodeURIComponent(fileId)}`;
    const response = await fetch(url, { method: 'DELETE', headers: authHeaders(token) });
    if (response.status === 404)
        return;
    await ensureOk(response, 'delete');
}
export async function uploadJson(token, folderId, fileName, content, appProperties) {
    const boundary = `---skjv-${Math.random().toString(36).slice(2)}`;
    const meta = { name: fileName, parents: [folderId] };
    if (appProperties)
        meta.appProperties = appProperties;
    const body = `--${boundary}\r\n` +
        `Content-Type: application/json; charset=UTF-8\r\n\r\n` +
        `${JSON.stringify(meta)}\r\n` +
        `--${boundary}\r\n` +
        `Content-Type: application/json\r\n\r\n` +
        `${content}\r\n` +
        `--${boundary}--`;
    const response = await fetch(`${DRIVE_UPLOAD}?uploadType=multipart&fields=id`, {
        method: 'POST',
        headers: { ...authHeaders(token), 'Content-Type': `multipart/related; boundary=${boundary}` },
        body,
    });
    await ensureOk(response, 'upload');
    const data = (await response.json());
    return data.id;
}
export async function updateJson(token, fileId, content) {
    const url = `${DRIVE_UPLOAD}/${encodeURIComponent(fileId)}?uploadType=media`;
    const response = await fetch(url, {
        method: 'PATCH',
        headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
        body: content,
    });
    await ensureOk(response, 'update');
}
export async function uploadBlob(token, folderId, fileName, blob) {
    const initResponse = await fetch(`${DRIVE_UPLOAD}?uploadType=resumable&fields=id`, {
        method: 'POST',
        headers: {
            ...authHeaders(token),
            'Content-Type': 'application/json',
            'X-Upload-Content-Type': blob.type || 'application/octet-stream',
        },
        body: JSON.stringify({ name: fileName, parents: [folderId] }),
    });
    await ensureOk(initResponse, 'uploadBlob:init');
    const uploadUrl = initResponse.headers.get('Location');
    if (!uploadUrl)
        throw new StorageError('no upload Location header', PROVIDER_ID, 'uploadBlob');
    const putResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': blob.type || 'application/octet-stream' },
        body: blob,
    });
    await ensureOk(putResponse, 'uploadBlob');
    const data = (await putResponse.json());
    return data.id;
}
//# sourceMappingURL=drive-client.js.map